import React, { useState, useEffect } from "react";
import { Table, Input } from "antd";
import { CheckOutlined } from '@ant-design/icons';
import { ref, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage'
import UnconfirmedEventsModal from './UnconfirmedEventsModal'
import isEqual from 'lodash.isequal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { db, storage } from '../../../db/db';

dayjs.extend(timezone);
dayjs.extend(utc)

function UnconfirmedTable({ committeeData }) {
  const [unconData, setUnconData] = useState([]);
  const [info, setInfo] = useState({});
  const [previousData, setPreviousData] = useState();
  const [changeType, setChangeType] = useState();
  const [open, setOpen] = useState(false);
  const [searchedText, setSearchedText] = useState("");
  const [itemsToDelete, setItemsToDelete] = useState([])
  const [fileList, setFileList] = useState()
  const [submitting, setSubmitting] = useState();
  const [quillValue, setQuillValue] = useState('');
  const [noDate, setNoDate] = useState(false);
  const [uponAdjourn, setUponAdjourn] = useState(false);
  const [useAgenda, setUseAgenda] = useState(false);
  const [loading, setLoading] = useState(false)

  const unconfirmedEventCol = [
    {
      title: 'Change Type',
      dataIndex: 'changeType',
      key: 'changeType',
      width: 65,
    },
    {
      title: 'Subject',
      key: 'subject',
      dataIndex: 'subject',
      width: 250,
      filteredValue: [searchedText],
      onFilter: (value, record) => {
        return String(record.billsResolutions)
          .toLowerCase()
          .includes(value.toLowerCase()) ||
          String(record.committees)
            .toLowerCase()
            .includes(value.toLowerCase()) ||
          String(record.start)
            .toLowerCase()
            .includes(value.toLowerCase())
      }
    },
    {
      title: 'Date/Time',
      dataIndex: 'start',
      key: 'start',
      width: 100,
      render: date => date ? dayjs(date).utc().tz('America/New_York').format("YYYY-MM-DD hh:mm a") : null
    },
    {
      title: 'Location',
      key: 'location',
      dataIndex: 'location',
      width: 100,
    },
    {
      title: 'Bills/Resolutions',
      key: 'billsResolutions',
      dataIndex: 'billsResolutions',
      width: 200,
    },
    {
      title: 'Agenda Link',
      key: 'useAgendaLink',
      dataIndex: 'useAgenda',
      width: 63,
      ellipsis: true,
      render: (useAgenda, record) =>
        !useAgenda && record.agendaUri ? <CheckOutlined /> : null,
    },
    {
      title: 'Agenda',
      key: 'useAgenda',
      dataIndex: 'useAgenda',
      width: 50,
      ellipsis: true,
      render: (useAgenda, record) =>
        useAgenda && record.agenda ? <CheckOutlined /> : null,
    },
    {
      title: 'Docs #',
      key: 'docCount',
      dataIndex: 'docCount',
      width: 50,
      ellipsis: true,
    },
  ];

  const getEventData = () => {
    setLoading(true)
    const unconfirmedArr = [];
    db.collection('unconfirmedEvents').get()
      .then(result => result.docs)
      .then(docs => docs.map(doc => doc.data()))
      .then(async (docs) => {
        for (let i = 0; i < docs.length; i += 1) {
          const unconfirmedDoc = docs[i];
          await db.collection('trackedEvents').doc(unconfirmedDoc.id).get()
            .then(result => result.data())
            .then(trackedData => {
              let isAgenda = false;
              let isAgendaLink = false;
              let quill = null;
              const docsArr = [];

              // Set agenda-related values
              if (trackedData?.agenda) {
                isAgenda = trackedData.agenda !== "\"<p><br></p>\"";
                quill = trackedData.agenda.startsWith('"') ? JSON.parse(trackedData.agenda) : trackedData.agenda;
              }

              if (trackedData?.agendaUri) {
                isAgendaLink = true;
              }

              // Collect extra documents
              if (trackedData?.extraDocuments?.length > 0) {
                trackedData.extraDocuments.forEach(doc => docsArr.push(doc));
              }

              unconfirmedArr.push({
                ...unconfirmedDoc,
                key: unconfirmedDoc.id,
                pageType: 'Event',
                billsResolutions: Array.from(
                  new Set([
                    ...(unconfirmedDoc.billsResolutions || []), 
                    ...(trackedData?.billsResolutions || [])
                  ])
                ).join(", "),
                agendaBool: isAgenda,
                agenda: quill,
                agendaLinkBool: isAgendaLink,
                committees: unconfirmedDoc.committees || [],
                docCount: trackedData?.extraDocuments?.length || 0,
                extraDocuments: docsArr,
                trackedSubject: trackedData?.subject || null,
                trackedStart: trackedData?.start || null,
                trackedEnd: trackedData?.end || null,
                trackedLocation: trackedData?.location || null,
                trackedLivestreamUrl: trackedData?.livestreamUrl || null,
                trackedAgendaLink: trackedData?.agendaUri || null,
                agendaNotes: trackedData?.agendaNotes || '',
                previousData: trackedData || {},
                noDate: unconfirmedDoc.start ? true : unconfirmedDoc.noDate || false,
              });
            });
        }
      })
      .then(arr => {
        unconfirmedArr.sort(function (a, b) {
          var dateA = new Date(a.start);
          var dateB = new Date(b.start);
          if (!b.start) {
            return 1;
          }
          if (!a.start) {
            return -1;
          }
          return dateB - dateA;
        });
        setUnconData(unconfirmedArr)
        setLoading(false)
      })
  }

  useEffect(() => {
    getEventData()
  }, [])

  const rowClicked = (e, record) => {
    const data = {
      ...record,
      start: record.start || dayjs().utc().tz('America/New_York'),
      end: record.end || dayjs(record.start).utc().tz('America/New_York').add(30, 'minute'),
    };

    // Set previousData only if the changeType is "updated"
    if (record.changeType === "updated") {
      setPreviousData(record.previousData || {});
      setChangeType("updated")
    } else {
      setPreviousData({})
    }

    setQuillValue(record.agenda || "");
    setNoDate(record.start ? false : record.noDate || false);
    setUponAdjourn(record.uponAdjourn || false);
    setInfo(data);
    setItemsToDelete([]);
  };

  const onDeleteEvent = (id) => {
    db.collection('unconfirmedEvents').doc(id).delete()
      .then(() => {
        setPreviousData();
        setQuillValue('');
        setChangeType();
        setFileList([]);
        setInfo({})
        getEventData();
        setOpen(false);
      })
  };

  const handleChange = ({ fileList }) =>
    setFileList(fileList.filter(file => file.status !== "error"));

  const onRemove = async file => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);

    setFileList(newFileList);
  };

  const storageUpload = async (saveData) => {
    setSubmitting(true);
    if (fileList?.length > 0) {
      const fileArr = [];
      await Promise.all(
        fileList?.map(async file => {
          const fileName = `${file.name}-${Date.now()}`
          const pdfRef = ref(storage, `${saveData.id}/${fileName}`);
          try {
            await uploadBytes(pdfRef, file.originFileObj)
            const downloadUrl = await getDownloadURL(pdfRef);
            const item = {
              name: fileName,
              url: downloadUrl,
            };
            fileArr.push(item)
          } catch (e) {
            console.log(e);
          }
        })
      )
      return fileArr
    } else {
      return []
    }
  }

  const onSubmit = async (saveData) => {
    const updateFields = {
      start: 'Date/Time',
      adjournText: 'Date/Time',
      agendaUri: 'Agenda',
      agendaNotes: 'Agenda Notes',
      agenda: 'Agenda',
      livestreamUrl: 'Livestream Link',
      location: 'Location',
      subject: 'Subject',
      billsResolutions: 'Bills',
      committees: 'Committees',
      extraDocuments: 'Extra Documents'
    };

    let updateObj = null;
    if (changeType === "updated") {
      const updates = [...new Set(Object.keys(saveData)
        .filter(key => updateFields[key] && !isEqual(
          saveData[key] ?? "",
          previousData[key] ?? ""
        ))
        .map(key => updateFields[key])
      )];

      if (updates.length) {
        const options = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York'
        };
        const formattedDate = new Intl.DateTimeFormat('en-US', options).format(new Date());
        updateObj = {
          date: formattedDate,
          updates,
        };
      }
    }

    try {
      const storedFiles = await storageUpload(saveData);
      const startDate = dayjs(saveData.start).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      const endDate = dayjs(saveData.end).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      const bills = (saveData.billsResolutions || '')  // Ensure it's a string
        .replace(/\s/g, '')  // Remove spaces
        .split(',')          // Split into array
        .filter(bill => bill); // Remove empty strings
      const formattedBills = bills.length ? bills : null;
      let quillStore = quillValue && quillValue !== "<p><br></p>" ? quillValue : null;

      saveData.key = saveData.id;
      saveData.pageType = 'Event';

      // Create the object to store in the database
      const eventData = {
        autoUpdate: saveData.autoUpdate,
        dontNotify: !saveData.notifyUsers,
        id: saveData.id,
        agendaUri: saveData.agendaUri || null,
        livestreamUrl: saveData.livestreamUrl || null,
        location: saveData.location || null,
        start: saveData.start ? startDate : null,
        end: noDate ? null : endDate,
        subject: saveData.subject || null,
        committees: saveData.committees || null,
        billsResolutions: formattedBills,
        body: info.body || null,
        chamber: info.chamber || null,
        isVimeo: info.isVimeo || null,
        willBroadcast: info.willBroadcast || null,
        extraDocuments: info.extraDocuments ? [...storedFiles, ...info.extraDocuments] : [...storedFiles],
        agenda: quillStore || null,
        noDate: noDate,
        uponAdjourn: noDate ? false : uponAdjourn,
        adjournText: saveData.adjournText || null,
        useAgenda: useAgenda,
        agendaNotes: saveData.agendaNotes || '',
      };

      // Only add updates if changeType is "updated" and there are actual updates
      if (updateObj) {
        eventData.updates = [updateObj, ...(previousData.updates || [])];
      } else if (previousData.updates) {
        eventData.updates = previousData.updates;
      }

      await db.collection('trackedEvents').doc(info.id).set(eventData);

      await db.collection('unconfirmedEvents').doc(info.id).delete();

      getEventData();
      setOpen(false);
      setPreviousData();
      setChangeType();
      setFileList([]);
      setQuillValue('');
      setInfo({})
    } catch (err) {
      console.error(err);
      console.log(`Error adding pdf.`, 2);
    } finally {
      setSubmitting(false);
      onSubmitDelete();
    }
  };

  const handleDelete = (fileName) => {
    const newArr = [...itemsToDelete, fileName]
    setItemsToDelete(newArr)
    setInfo({
      ...info,
      extraDocuments: info.extraDocuments.filter(item => item.name !== fileName)
    })
  };

  const onSubmitDelete = () => {
    for (let i = 0; i < itemsToDelete.length; i += 1) {
      const pdfRef = ref(storage, `${info.id}/${itemsToDelete[i]}`)
      deleteObject(pdfRef).then(() => {
        // File deleted successfully
      })
    }
    setItemsToDelete([])
  };

  const centerPagination = "bottomCenter";
  return (
    <div>
      <Input.Search
        placeholder='Search Unconfirmed Events'
        style={{ marginBottom: 8 }}
        onSearch={(value) => {
          setSearchedText(value);
        }}
        onChange={(e) => {
          setSearchedText(e.target.value)
        }}
      />
      <Table
        onRow={(record) => ({
          onClick: (event) => {
            rowClicked(event, record);
          },
        })}
        bordered
        size={'small'}
        loading={loading}
        columns={unconfirmedEventCol}
        dataSource={unconData}
        pagination={{
          position: [centerPagination],
          hideOnSinglePage: true,
        }}
        scroll={{ y: 500 }}
        footer={() =>
          <UnconfirmedEventsModal
            info={info}
            setInfo={setInfo}
            tableType={'Unconfirmed Event'}
            onSubmit={onSubmit}
            onDeleteEvent={onDeleteEvent}
            open={open}
            setOpen={setOpen}
            committeeData={committeeData}
            handleDelete={handleDelete}
            handleChange={handleChange}
            onRemove={onRemove}
            submitting={submitting}
            setFileList={setFileList}
            fileList={fileList}
            quillValue={quillValue}
            setQuillValue={setQuillValue}
            noDate={noDate}
            setNoDate={setNoDate}
            uponAdjourn={uponAdjourn}
            setUponAdjourn={setUponAdjourn}
            useAgenda={useAgenda}
            setUseAgenda={setUseAgenda}
          />
        }
      />
    </div>
  );
}

export default UnconfirmedTable;